import type {
  AcademicRiskStatistics,
  ResultUIStudentAcademicRisk,
  StudentAcademicRisk,
} from '@/domains/academicRisk/academicRiskStudent.entity';

/**
 * Datos procesados de riesgo académico listos para UI
 */
interface ProcessedAcademicRiskData {
  studentsForUI: ResultUIStudentAcademicRisk[];
  statistics: AcademicRiskStatistics;
}

/**
 * Use Case para procesar datos de estudiantes de AcademicRisk
 *
 * Responsabilidades:
 * - Transformar estudiantes de entidad de dominio a formato UI
 * - Procesar riesgos identificados en arrays
 * - Calcular estadísticas agregadas (total, en riesgo, porcentajes, casos activos)
 * - Validar integridad de datos de entrada
 */
export class ProcessAcademicRiskStudentDataUseCase {
  /**
   * Transforma un estudiante de entidad de dominio a formato UI
   * Procesa el string de riesgos_identificados en un array y cuenta los riesgos
   * @param student - Estudiante en formato de entidad de dominio
   */
  private transformStudentToUI(
    student: StudentAcademicRisk,
  ): ResultUIStudentAcademicRisk {
    // Procesar riesgos identificados
    const riskArray = student.riesgos_identificados
      ? student.riesgos_identificados
          .split(',')
          .map((r) => r.trim())
          .filter((r) => r.length > 0)
      : [];

    return {
      ...student,
      riskArray,
      riskCount: riskArray.length,
    };
  }

  /**
   * Calcula estadísticas agregadas de los estudiantes procesados
   * Incluye total, estudiantes en riesgo, porcentaje y casos activos (asignados)
   * @param students - Array de estudiantes ya transformados a formato UI
   */
  private calculateStatistics(
    students: ResultUIStudentAcademicRisk[],
  ): AcademicRiskStatistics {
    const totalNumberStudents = students.length;
    const StudentAtRisk = students.filter((s) => s.riskCount > 0).length;
    const percentageAtRisk =
      totalNumberStudents > 0 ? (StudentAtRisk / totalNumberStudents) * 100 : 0;

    const activeCases = students.filter(
      (s) => s.estado.toLowerCase() === 'asignado',
    ).length;

    return {
      totalNumberStudents,
      StudentAtRisk,
      percentageAtRisk: Math.round(percentageAtRisk * 100) / 100,
      activeCases,
    };
  }

  /**
   * Valida que los datos de entrada sean un array válido con elementos
   * @param students - Datos de estudiantes a validar
   */
  private isValidStudentData(students: StudentAcademicRisk[]): boolean {
    return Array.isArray(students) && students.length > 0;
  }

  /**
   * Ejecuta el procesamiento completo de estudiantes de riesgo académico
   * Transforma datos de entidad a UI y calcula estadísticas agregadas
   * @param students - Lista de estudiantes en formato de entidad de dominio
   */
  execute(students: StudentAcademicRisk[]): ProcessedAcademicRiskData {
    if (!this.isValidStudentData(students)) {
      return {
        studentsForUI: [],
        statistics: {
          totalNumberStudents: 0,
          StudentAtRisk: 0,
          percentageAtRisk: 0,
          activeCases: 0,
        },
      };
    }

    const studentsForUI = students.map((student) =>
      this.transformStudentToUI(student),
    );

    const statistics = this.calculateStatistics(studentsForUI);

    return {
      studentsForUI,
      statistics,
    };
  }
}
